import api from "../../services/api";

export interface ExtractionProposal {
  id: string;
  target: string;
  recordUid?: string | null;
  documentUid?: string;
  values: Record<string, unknown>;
  evidence?: unknown;
  status: string;
  result?: unknown;
}
export interface RecordComparison {
  operation: "new" | "unchanged" | "update" | "ambiguous";
  recordUid?: string | null;
  candidates: { uid: string; label: string; reasons: string[] }[];
  records: { uid: string; label: string }[];
  currentValues: Record<string, unknown>;
  fields: {
    key: string;
    current?: unknown;
    extracted?: unknown;
    change: "unchanged" | "fill" | "replace";
  }[];
  comparisonToken: string;
}
export interface SourceDocument {
  uid: string;
  filename: string;
  description?: string;
  documentType?: string;
  status: string;
  error?: string;
  createdAt: string;
  proposals: ExtractionProposal[];
}
export type ReviewAction = "confirm" | "defer" | "reject";
export const sourceDocumentsService = {
  async list(estateId: string): Promise<SourceDocument[]> {
    return (await api.get(`/source-documents/${estateId}`)).data;
  },
  async original(estateId: string, uid: string): Promise<Blob> {
    return (
      await api.get(`/source-documents/${estateId}/${uid}/download`, {
        responseType: "blob",
      })
    ).data;
  },
  async text(
    estateId: string,
    uid: string
  ): Promise<{ text: string; method: string; warning?: string }> {
    return (await api.get(`/source-documents/${estateId}/${uid}/text`)).data;
  },
  async upload(
    estateId: string,
    files: File[],
    description: string
  ): Promise<void> {
    const data = new FormData();
    files.forEach((file) => data.append("files", file));
    if (description.trim()) data.append("description", description.trim());
    await api.post(`/source-documents/${estateId}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  async extract(estateId: string, uid: string): Promise<void> {
    await api.post(`/source-documents/${estateId}/${uid}/extract`);
  },
  async describe(
    estateId: string,
    uid: string,
    description: string
  ): Promise<void> {
    await api.patch(`/source-documents/${estateId}/${uid}`, { description });
  },
  async remove(estateId: string, uid: string): Promise<void> {
    await api.delete(`/source-documents/${estateId}/${uid}`);
  },
  async download(estateId: string, document: SourceDocument): Promise<void> {
    const response = await api.get(
      `/source-documents/${estateId}/${document.uid}/download`,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(response.data);
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = document.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },
  async compare(
    estateId: string,
    uid: string,
    proposal: ExtractionProposal,
    values: Record<string, unknown>
  ): Promise<RecordComparison> {
    return (
      await api.post(`/source-documents/${estateId}/${uid}/compare`, {
        proposalId: proposal.id,
        target: proposal.target,
        values,
        ...(proposal.recordUid !== undefined
          ? { recordUid: proposal.recordUid }
          : {}),
        documentUid: proposal.documentUid,
      })
    ).data;
  },
  async review(
    estateId: string,
    uid: string,
    proposal: ExtractionProposal,
    action: ReviewAction,
    values?: Record<string, unknown>,
    comparisonToken?: string
  ): Promise<void> {
    await api.post(`/source-documents/${estateId}/${uid}/review`, {
      proposalId: proposal.id,
      action,
      ...(action === "confirm"
        ? {
            target: proposal.target,
            values,
            comparisonToken,
            recordUid: proposal.recordUid ?? null,
            documentUid: proposal.documentUid,
          }
        : {}),
    });
  },
};
